'use client'

import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DynamicIcon, iconNames } from 'lucide-react/dynamic'
import { useVirtualizer } from '@tanstack/react-virtual'
import Fuse from 'fuse.js'
import { useDebounceValue } from 'usehooks-ts'
import type { LucideIcon, LucideProps } from 'lucide-react'
import type { IconName } from 'lucide-react/dynamic'
import type { VirtualItem } from '@tanstack/react-virtual'
import type { IconData } from './icons-data'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * Props interface for the IconPicker component.
 * Extends PopoverTrigger props while omitting conflicting properties.
 */
interface IconPickerProps
  extends Omit<
    React.ComponentPropsWithoutRef<typeof PopoverTrigger>,
    'onSelect' | 'onOpenChange' | 'value' | 'defaultValue'
  > {
  /** Currently selected icon name */
  value?: IconName | null
  /** Default icon name for uncontrolled usage */
  defaultValue?: IconName | null
  /** Callback fired when icon selection changes */
  onValueChange?: (value: IconName | null) => void
  /** Whether picker is open (controlled) */
  open?: boolean
  /** Default open state for uncontrolled usage */
  defaultOpen?: boolean
  /** Callback fired when open state changes */
  onOpenChange?: (open: boolean) => void
  /** Whether to show search input */
  searchable?: boolean
  /** Placeholder text for search input */
  searchPlaceholder?: string
  /** Placeholder text for trigger button */
  triggerPlaceholder?: string
  /** Custom icons list to display */
  iconsList?: Array<IconData>
  /** Whether to organize icons by categories */
  categorized?: boolean
  /** Whether to use modal behavior */
  modal?: boolean
}

/** Set of all available icon names from Lucide React */
const AVAILABLE_ICON_NAMES = new Set<IconName>(iconNames)

/**
 * Type guard to check if a string is a valid Lucide icon name.
 *
 * @param name - The string to validate
 * @returns True if the name is a valid icon name
 */
export const isValidIconName = (
  name: string | null | undefined,
): name is IconName => !!name && AVAILABLE_ICON_NAMES.has(name as IconName)

/**
 * Normalizes and validates an icon name, returning null for invalid names.
 *
 * @param name - The icon name to normalize
 * @returns Valid icon name or null if invalid
 */
export const normalizeIconName = (
  name: string | null | undefined,
): IconName | null => (isValidIconName(name) ? name : null)

const IconRenderer = React.memo(({ name }: { name: IconName }) => {
  return <Icon name={name} />
})
IconRenderer.displayName = 'IconRenderer'

const IconsColumnSkeleton = () => {
  return (
    <div className="flex flex-col gap-2 w-full">
      <Skeleton className="h-4 w-1/2 rounded-md" />
      <div className="grid grid-cols-5 gap-2 w-full">
        {Array.from({ length: 40 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-10 rounded-md" />
        ))}
      </div>
    </div>
  )
}

const useIconsData = () => {
  const [icons, setIcons] = useState<Array<IconData>>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isMounted = true

    const loadIcons = async () => {
      setIsLoading(true)

      const { iconsData } = await import('./icons-data')
      if (isMounted) {
        const filteredIcons = iconsData.filter((icon) =>
          AVAILABLE_ICON_NAMES.has(icon.name as IconName),
        )
        setIcons(filteredIcons)
        setIsLoading(false)
      }
    }

    loadIcons()

    return () => {
      isMounted = false
    }
  }, [])

  return { icons, isLoading }
}

const IconPicker = React.forwardRef<
  React.ComponentRef<typeof PopoverTrigger>,
  IconPickerProps
>((props, ref) => {
  const { t } = useTranslation('common')
  const {
    value,
    defaultValue,
    onValueChange,
    open,
    defaultOpen,
    onOpenChange,
    children,
    searchable = true,
    searchPlaceholder = 'Search for an icon...',
    triggerPlaceholder = 'Select an icon',
    iconsList,
    categorized = true,
    modal = false,
    ...rest
  } = props

  const isValueControlled = Object.prototype.hasOwnProperty.call(props, 'value')
  const isOpenControlled = Object.prototype.hasOwnProperty.call(props, 'open')

  const [selectedIcon, setSelectedIcon] = useState<IconName | null>(
    normalizeIconName(defaultValue),
  )
  const [internalOpen, setInternalOpen] = useState(defaultOpen ?? false)
  const [search, setSearch] = useDebounceValue('', 100)
  const [isPopoverVisible, setIsPopoverVisible] = useState(false)
  const { icons } = useIconsData()
  const [isLoading, setIsLoading] = useState(true)

  const iconsToUse = useMemo(() => {
    const baseIcons = iconsList ?? icons
    return baseIcons.filter((icon) =>
      AVAILABLE_ICON_NAMES.has(icon.name as IconName),
    )
  }, [iconsList, icons])

  const fuseInstance = useMemo(() => {
    return new Fuse(iconsToUse, {
      keys: ['name', 'tags', 'categories'],
      threshold: 0.3,
      ignoreLocation: true,
      includeScore: true,
    })
  }, [iconsToUse])

  const filteredIcons = useMemo(() => {
    if (search.trim() === '') {
      return iconsToUse
    }

    const results = fuseInstance.search(search.toLowerCase().trim())
    return results.map((result) => result.item)
  }, [search, iconsToUse, fuseInstance])

  const categorizedIcons = useMemo(() => {
    if (!categorized || search.trim() !== '') {
      return [{ name: 'All Icons', icons: filteredIcons }]
    }

    const categories = new Map<string, Array<IconData>>()

    filteredIcons.forEach((icon) => {
      if (icon.categories.length > 0) {
        icon.categories.forEach((category) => {
          if (!categories.has(category)) {
            categories.set(category, [])
          }
          categories.get(category)!.push(icon)
        })
      } else {
        const category = 'Other'
        if (!categories.has(category)) {
          categories.set(category, [])
        }
        categories.get(category)!.push(icon)
      }
    })

    return Array.from(categories.entries())
      .map(([name, icons]) => ({ name, icons }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [filteredIcons, categorized, search])

  const virtualItems = useMemo(() => {
    const items: Array<{
      type: 'category' | 'row'
      categoryIndex: number
      rowIndex?: number
      icons?: Array<IconData>
    }> = []

    categorizedIcons.forEach((category, categoryIndex) => {
      items.push({ type: 'category', categoryIndex })

      const rows = []
      for (let i = 0; i < category.icons.length; i += 5) {
        rows.push(category.icons.slice(i, i + 5))
      }

      rows.forEach((rowIcons, rowIndex) => {
        items.push({
          type: 'row',
          categoryIndex,
          rowIndex,
          icons: rowIcons,
        })
      })
    })

    return items
  }, [categorizedIcons])

  const categoryIndices = useMemo(() => {
    const indices: Record<string, number> = {}

    virtualItems.forEach((item, index) => {
      const category =
        item.type === 'category' ? categorizedIcons[item.categoryIndex] : undefined
      if (category) {
        indices[category.name] = index
      }
    })

    return indices
  }, [virtualItems, categorizedIcons])

  const parentRef = React.useRef<HTMLDivElement>(null)

  const virtualizer = useVirtualizer({
    count: virtualItems.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => (virtualItems[index]?.type === 'category' ? 25 : 40),
    paddingEnd: 2,
    gap: 10,
    overscan: 5,
  })

  useEffect(() => {
    if (isValueControlled) {
      setSelectedIcon(normalizeIconName(value))
    }
  }, [isValueControlled, value])

  useEffect(() => {
    if (!isValueControlled && defaultValue !== undefined) {
      setSelectedIcon(normalizeIconName(defaultValue))
    }
  }, [defaultValue, isValueControlled])

  const handleValueChange = useCallback(
    (icon: IconName | null) => {
      const normalizedIcon = normalizeIconName(icon)

      if (!isValueControlled) {
        setSelectedIcon(normalizedIcon)
      }
      onValueChange?.(normalizedIcon)
    },
    [isValueControlled, onValueChange],
  )

  const setOpenState = useCallback(
    (nextOpen: boolean) => {
      setSearch('')
      if (!isOpenControlled) {
        setInternalOpen(nextOpen)
      }
      onOpenChange?.(nextOpen)
      setIsPopoverVisible(nextOpen)
    },
    [isOpenControlled, onOpenChange, setSearch],
  )

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setOpenState(newOpen)

      if (newOpen) {
        setTimeout(() => {
          virtualizer.measure()
          setIsLoading(false)
        }, 1)
      } else {
        setIsLoading(false)
      }
    },
    [setOpenState, virtualizer],
  )

  const handleIconClick = useCallback(
    (iconName: IconName) => {
      handleValueChange(iconName)
      setOpenState(false)
    },
    [handleValueChange, setOpenState],
  )

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearch(e.target.value)

      if (parentRef.current) {
        parentRef.current.scrollTop = 0
      }

      virtualizer.scrollToOffset(0)
    },
    [virtualizer],
  )

  const scrollToCategory = useCallback(
    (categoryName: string) => {
      const categoryIndex = categoryIndices[categoryName]

      if (categoryIndex !== undefined) {
        virtualizer.scrollToIndex(categoryIndex, {
          align: 'start',
          behavior: 'smooth',
        })
      }
    },
    [categoryIndices, virtualizer],
  )

  const categoryButtons = useMemo(() => {
    if (!categorized || search.trim() !== '') return null

    return categorizedIcons.map((category) => (
      <Button
        key={category.name}
        variant={'outline'}
        size="sm"
        className="text-xs"
        onClick={(e) => {
          e.stopPropagation()
          scrollToCategory(category.name)
        }}
      >
        {category.name.charAt(0).toUpperCase() + category.name.slice(1)}
      </Button>
    ))
  }, [categorizedIcons, scrollToCategory, categorized, search])

  const currentSelection = useMemo<IconName | null>(() => {
    return normalizeIconName(isValueControlled ? value : selectedIcon)
  }, [isValueControlled, selectedIcon, value])

  const renderIcon = useCallback(
    (icon: IconData) => {
      const iconName = icon.name as IconName
      const isActive = currentSelection === iconName

      return (
        <TooltipProvider key={icon.name}>
          <Tooltip>
            <TooltipTrigger
              type="button"
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground transition-colors',
                isActive ? 'bg-primary/10 text-primary' : 'hover:bg-muted/60',
              )}
              onClick={() => handleIconClick(iconName)}
            >
              <IconRenderer name={iconName} />
            </TooltipTrigger>
            <TooltipContent>
              <p>{icon.name}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )
    },
    [currentSelection, handleIconClick],
  )

  const renderVirtualContent = useCallback(() => {
    if (filteredIcons.length === 0) {
      return <div className="text-center text-muted-foreground">{t('iconPicker.noIcon')}</div>
    }

    return (
      <div
        className="relative w-full overscroll-contain"
        style={{
          height: `${virtualizer.getTotalSize()}px`,
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem: VirtualItem) => {
          const item = virtualItems[virtualItem.index]
          // The virtualizer can report an index past the end mid-recompute; this
          // also lets `item.type` discriminate the row union below.
          if (!item) return null

          const itemStyle = {
            position: 'absolute' as const,
            top: 0,
            left: 0,
            width: '100%',
            height: `${virtualItem.size}px`,
            transform: `translateY(${virtualItem.start}px)`,
          }

          if (item.type === 'category') {
            return (
              <div
                key={virtualItem.key}
                style={itemStyle}
                className="top-0 bg-background z-10"
              >
                <h3 className="font-medium text-sm capitalize">
                  {categorizedIcons[item.categoryIndex]?.name}
                </h3>
                <div className="h-[1px] bg-foreground/10 w-full" />
              </div>
            )
          }

          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              style={itemStyle}
            >
              <div className="grid grid-cols-5 gap-2 w-full">
                {item.icons!.map(renderIcon)}
              </div>
            </div>
          )
        })}
      </div>
    )
  }, [virtualizer, virtualItems, categorizedIcons, filteredIcons, renderIcon])

  const handleClearSelection = useCallback(() => {
    handleValueChange(null)
    setOpenState(false)
  }, [handleValueChange, setOpenState])

  React.useEffect(() => {
    if (isPopoverVisible) {
      setIsLoading(true)
      const timer = setTimeout(() => {
        setIsLoading(false)
        virtualizer.measure()
      }, 10)

      const resizeObserver = new ResizeObserver(() => {
        virtualizer.measure()
      })

      if (parentRef.current) {
        resizeObserver.observe(parentRef.current)
      }

      return () => {
        clearTimeout(timer)
        resizeObserver.disconnect()
      }
    }
  }, [isPopoverVisible, virtualizer])

  const currentOpen = isOpenControlled ? (open ?? false) : internalOpen

  return (
    <Popover open={currentOpen} onOpenChange={handleOpenChange} modal={modal}>
      <PopoverTrigger ref={ref} asChild {...rest}>
        {children || (
          <Button variant="outline">
            {currentSelection ? (
              <>
                <Icon name={currentSelection} /> {currentSelection}
              </>
            ) : (
              triggerPlaceholder
            )}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3">
        {searchable && (
          <Input
            placeholder={searchPlaceholder}
            onChange={handleSearchChange}
            className="mb-2"
          />
        )}
        {categorized && search.trim() === '' && (
          <div className="flex flex-row gap-1 mt-2 overflow-x-auto pb-2">
            {categoryButtons}
          </div>
        )}
        <div
          ref={parentRef}
          className="max-h-60 overflow-auto [scrollbar-width:thin]"
        >
          {isLoading ? <IconsColumnSkeleton /> : renderVirtualContent()}
        </div>
        {currentSelection && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClearSelection}
            className="mt-2 w-full justify-center text-xs text-muted-foreground hover:text-destructive"
          >
            Clear icon
          </Button>
        )}
      </PopoverContent>
    </Popover>
  )
})
IconPicker.displayName = 'IconPicker'

interface IconProps extends Omit<LucideProps, 'ref'> {
  name: IconName
}

const Icon = React.forwardRef<React.ComponentRef<LucideIcon>, IconProps>(
  ({ name, ...props }, ref) => {
    return <DynamicIcon name={name} {...props} ref={ref} />
  },
)
Icon.displayName = 'Icon'

export { IconPicker, Icon, type IconName }
